import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { Fade } from '@mui/material';
import { Box, Button, Card, Container, Stack, Typography, styled, Link } from '@mui/material';

import useResponsive from '../hooks/useResponsive';
import Page from '../components/Page';
import BasicBreadcrumb from '../components/BasicBreadcrumb';
import LoadingInfo from '../sections/people/LoadingInfo';
import KeyValueDisplay from '../components/KeyValueDisplay';
import KeyValuesDisplay from '../components/KeyValuesDisplay';
import KeyValueLabelDisplay from '../components/KeyValueLabelDisplay';
import Label from '../components/Label';
import { UserGQLFunction } from '../graphql/USER';
import { USER } from '../info/USER';

const RootStyle = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center'
}));
const ContentStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  margin: theme.spacing(2),
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'column',
  padding: theme.spacing(2)
}));

const DataDisplay = ({ data, smUp, t }) => (
  <Box
    sx={{
      display: 'grid',
      columnGap: 2,
      rowGap: 3,
      gridTemplateColumns: smUp ? 'repeat(2, 1fr)' : '1fr',
      m: 1,
      mb: 4
    }}
  >
    <KeyValueDisplay title={t('common.id')} data={data.userInfo.id} />
    <KeyValueDisplay title={t('common.user_id')} data={data.userInfo.userId} />
    <KeyValueDisplay title={t('common.username')} data={data.userInfo.username} />
    <KeyValueDisplay title={t('common.nickname')} data={data.userInfo.nickname || '-'} />
    <KeyValueDisplay title={t('common.email')} data={data.userInfo.email} />
    <KeyValueDisplay title={t('common.title')} data={data.userInfo.title || '-'} />
    <KeyValueLabelDisplay title={t('common.user_type')} data={t(`common.${USER.USER_TYPE[data.userInfo.userType]}`)} color={'default'} />
    <KeyValueLabelDisplay
      title={t('common.user_state')}
      data={t(`common.${USER.USER_STATE[data.userInfo.userState]}`)}
      color={!data.userInfo.userState ? 'success' : 'warning'}
    />
    <KeyValuesDisplay title={t('common.device_ids')} data={data.userInfo.deviceIds} />
    <KeyValuesDisplay
      title={t('common.push_ids')}
      data={
        data.userInfo && data.userInfo.pushIds && data.userInfo.pushIds.length > 0
          ? data.userInfo.pushIds.map((pushId, index) => (
            <Label variant={'ghost'} color={'info'} key={index}>
              <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap', width: '100%', maxWidth: 400, display: 'block' }}>
                <Typography fontFamily="monospace">{pushId}</Typography>
              </Box>
            </Label>
          ))
          : null
      }
    />
  </Box>
);

export default function UserInfo() {
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const { loading: queryLoading, data: queryData } = useQuery(UserGQLFunction.GET_USER_INFO, {
    variables: { id },
    fetchPolicy: 'network-only'
  });

  const [deleteUser] = useMutation(UserGQLFunction.DELETE_USER, {
    variables: { id },
    onCompleted: () => {
      enqueueSnackbar(t('common.success'), { variant: 'success' });
      navigate('/user', { replace: true });
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const bread = [
    <Link component={RouterLink} to="/user" underline="hover" key="1" color="inherit">
      {t('nav.users')}
    </Link>,
    <Typography key="2" color="text.primary">
      {t('people.info_title', { identity: t('nav.users') })}
    </Typography>
  ];

  const identityName = t('nav.users');
  const userId = queryData?.userInfo?.userId;

  return (
    <Page title={t('people.info_title', { identity: identityName })}>
      <Container maxWidth={false}>
        <Stack direction="column">
          <Typography variant="h4" gutterBottom>
            {t('people.info_title', { identity: identityName })}
          </Typography>
          <BasicBreadcrumb bread={bread} />
        </Stack>

        <RootStyle>
          <ContentStyle>
            {!queryLoading && queryData ? <DataDisplay data={queryData} smUp={smUp} t={t} /> : <LoadingInfo smUp={smUp} />}
            <Stack direction={smUp ? 'row' : 'column'} justifyContent="space-between" alignItems="center" spacing={2}>
              <Stack direction={smUp ? 'row' : 'column'} spacing={2} sx={{ width: '400px' }}>
                {userId !== 'system_manager' && (
                  <Button
                    sx={{ maxWidth: smUp ? '350px' : '100%' }}
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      navigate(`/user/info/edit/${id}`);
                    }}
                  >
                    {t('common.edit')}
                  </Button>
                )}
                {userId !== 'system_manager' && (
                  <Button
                    sx={{ maxWidth: smUp ? '350px' : '100%' }}
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      navigate(`/user/history?userId=${userId}&id=${id}`);
                    }}
                  >
                    {t('history.title')}
                  </Button>
                )}
              </Stack>
              {userId !== 'system_manager' && (
                <Button
                  sx={{ maxWidth: smUp ? '200px' : '100%' }}
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    confirm({
                      title: t('people.delete_confirm_title', { identity: identityName }),
                      description: t('people.delete_confirm_desc', { identity: identityName }),
                      confirmationText: t('common.delete'),
                      cancellationText: t('common.cancel'),
                      confirmationButtonProps: { color: 'error' }
                    })
                      .then(() => {
                        deleteUser();
                      })
                      .catch(() => {
                        enqueueSnackbar(t('people.delete_cancel', { identity: identityName }), {
                          TransitionComponent: Fade,
                          preventDuplicate: true,
                          variant: 'warning'
                        });
                      });
                  }}
                >
                  {t('common.delete')}
                </Button>
              )}
            </Stack>
          </ContentStyle>
        </RootStyle>
      </Container>
    </Page>
  );
}
